import axios, { Canceler, Method, AxiosError } from 'axios'
import errors, { AppError } from '../error'
import { Context, InferNative, Type } from '..'
import {
  BaseActionDefinitionEnhanced,
  BaseActionDefinition,
  Trigger,
  HttpTrigger,
} from '../actionDefinition'
import { auth } from '~/utils/client/auth'

export interface ServerlessSourceOptions {
  baseUrl: string
  onError?: (error: Error) => void
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function resolveTriggerUrlAndInput<Input>(
  baseUrl: string,
  trigger: HttpTrigger,
  input: Input,
): { url: string; inputWithoutRouteParameters: Input | Record<string, unknown> } {
  if (!trigger.route.includes('/:')) {
    return {
      url: `${baseUrl}${trigger.route}`,
      inputWithoutRouteParameters: input,
    }
  }

  if (!isRecord(input)) {
    throw errors.incompatible(
      `Ação com rota "${trigger.route}" precisa receber um objeto para interpolar parâmetros.`,
    )
  }

  const inputWithoutRouteParameters = { ...input }
  const resolvedRoute = trigger.route.replace(/:([^/]+)/g, routeParameterExpression => {
    const routeParameterName = routeParameterExpression.slice(1)
    const routeParameterValue = inputWithoutRouteParameters[routeParameterName]

    if (routeParameterValue === undefined || routeParameterValue === null) {
      throw errors.incompatible(
        `Parâmetro "${routeParameterName}" ausente para montar rota "${trigger.route}".`,
      )
    }

    delete inputWithoutRouteParameters[routeParameterName]
    return encodeURIComponent(String(routeParameterValue))
  })

  return {
    url: `${baseUrl}${resolvedRoute}`,
    inputWithoutRouteParameters,
  }
}

export default function serverlessSource({
  baseUrl,
  onError = (error: Error) => console.error(error),
}: ServerlessSourceOptions) {
  const callAction = <
    InputType extends Type,
    OutputType extends Type,
    Input = InferNative<InputType>,
    Output = InferNative<OutputType>,
    CurrentActionDefinition extends BaseActionDefinition = BaseActionDefinition,
  >(
    definition: BaseActionDefinitionEnhanced<InputType, OutputType>,
    input: Input,
    context: Context,
    onAbort = () => undefined,
  ) => {
    let isAborted = false

    const firstTrigger = definition.triggers.find(
      trigger => trigger.trigger === 'http',
    ) as HttpTrigger
    if (!firstTrigger) {
      throw errors.incompatible(`Ação "${definition.key}" não pode ser chamada neste método`)
    }

    const headers = {
      Accept: 'application/json',
      ...(context.requestId ? { RequestId: context.requestId } : {}),
      ...(context.user?.activeClient?.clientId
        ? { 'X-Prix-Active-Client-Id': context.user.activeClient.clientId }
        : {}),
    }
    const { url, inputWithoutRouteParameters } = resolveTriggerUrlAndInput(
      baseUrl,
      firstTrigger,
      input,
    )

    let abortRequest: Canceler
    return {
      run: async () => {
        let token = context.token
        try {
          if (auth.currentUser) {
            token = await context.getToken()
          }
        } catch (error) {
          console.error(error)
        }
        try {
          const responsePromise = axios(url, {
            method: firstTrigger.method as Method,
            params: firstTrigger.method === 'GET' ? inputWithoutRouteParameters : undefined,
            data: firstTrigger.method !== 'GET' ? inputWithoutRouteParameters : undefined,
            headers: token ? { ...headers, Token: token } : headers,
            timeout: (definition.runtime.timeout || 15) * 1000,
            cancelToken: new axios.CancelToken(cancel => {
              abortRequest = cancel
            }),
          })

          const { data } = await responsePromise

          if (isAborted) {
            return
          }

          return data
        } catch (axiosError) {
          const error =
            (axiosError as AxiosError).response?.data &&
            typeof (axiosError as AxiosError).response!.data === 'object'
              ? (axiosError as AxiosError<AppError>).response!.data
              : (axiosError as Error)
          if (isAborted) {
            console.error(error)
            const abortError = errors.abort()
            onError(abortError)
            throw abortError
          }

          onError(error)
          throw error
        }
      },
      abort: async () => {
        isAborted = true
        if (abortRequest) {
          abortRequest()
        }
        onAbort()
      },
    }
  }

  return {
    kind: 'source' as 'source',
    checkAvailability: async () => {
      return navigator.onLine
    },
    call: callAction,
  }
}
